import type { RuntimeFlagName } from "@runcheetah/shared";

export type RuntimeFlagGroupId =
  | "brand_campaign"
  | "social_generation"
  | "publishing"
  | "billing_credits"
  | "model_pricing"
  | "llm_models"
  | "safety_detection"
  | "other";

export type RuntimeFlagGroupSlug =
  | "brand-campaign"
  | "social-generation"
  | "publishing"
  | "billing-credits"
  | "model-pricing"
  | "llm-models"
  | "safety-detection";

export type RuntimeFlagGroupDefinition = {
  id: Exclude<RuntimeFlagGroupId, "other">;
  slug: RuntimeFlagGroupSlug;
  title: string;
  description: string;
  flagNames: readonly RuntimeFlagName[];
};

type RuntimeFlagFallbackGroupDefinition = {
  id: "other";
  title: string;
  description: string;
  flagNames: readonly RuntimeFlagName[];
};

export type GroupedRuntimeFlags<TFlag extends { name: string }> = {
  id: RuntimeFlagGroupId;
  slug?: RuntimeFlagGroupSlug;
  title: string;
  description: string;
  flags: TFlag[];
};

export const RUNTIME_FLAG_ROOT_PATH = "/runtime-flags";

export const RUNTIME_FLAG_GROUPS = [
  {
    id: "brand_campaign",
    slug: "brand-campaign",
    title: "Brand & Campaign Automation",
    description: "Controls strategy refreshes and automatic campaign generation.",
    flagNames: [
      "brand_kit_strategy_regeneration_debounce_minutes",
      "campaign_autopilot_enabled",
      "brand_onboarding_mode",
    ],
  },
  {
    id: "social_generation",
    slug: "social-generation",
    title: "Social Post Generation",
    description: "Tunes social post planning, image realization, and generated image QA.",
    flagNames: [
      "social_post_image_realizer_provider",
      "social_post_image_candidate_count",
      "social_post_image_qa_enabled",
      "social_post_image_qa_retry_enabled",
      "social_post_image_qa_threshold_pct",
      "social_post_image_qa_failure_policy",
      "social_post_planner_freeform_enabled",
    ],
  },
  {
    id: "publishing",
    slug: "publishing",
    title: "Publishing & Autopublish",
    description: "Gates live publishing and brand-level automatic publishing behavior.",
    flagNames: [
      "social_publish_instagram_enabled",
      "social_autopublish_global_mode",
      "social_autopublish_enabled",
    ],
  },
  {
    id: "billing_credits",
    slug: "billing-credits",
    title: "Billing & AI Credits",
    description: "Controls customer billing visibility, entitlement grace, and AI run pricing.",
    flagNames: [
      "billing_enabled",
      "billing_failed_payment_grace_period_days",
      "signup_free_runs",
      "ai_credit_policy",
    ],
  },
  {
    id: "model_pricing",
    slug: "model-pricing",
    title: "Model Pricing Sync",
    description: "Configures model price ingestion and manual price overrides.",
    flagNames: [
      "pricing_sync_enabled",
      "pricing_sync_cron",
      "models_dev_api_url",
      "models_dev_provider_allow_list",
      "llm_pricing_overrides",
      "llm_materialized_prompt_sampling_rate",
    ],
  },
  {
    id: "llm_models",
    slug: "llm-models",
    title: "LLM Models",
    description: "Selects default models for API and worker generation paths.",
    flagNames: [
      "openai_chat_model",
      "brand_assistant_chat_context_window_tokens",
      "worker_openai_model",
      "worker_analysis_model",
      "worker_generation_model",
      "worker_openai_image_generation_model",
      "worker_social_post_image_qa_model",
      "worker_social_post_planner_model",
      "worker_social_post_copywriter_model",
    ],
  },
  {
    id: "safety_detection",
    slug: "safety-detection",
    title: "Safety & Detection",
    description: "Thresholds for media classifiers and safety-sensitive detection flows.",
    flagNames: ["real_people_detection_confidence_threshold_pct"],
  },
] as const satisfies readonly RuntimeFlagGroupDefinition[];

const OTHER_RUNTIME_FLAG_GROUP = {
  id: "other",
  title: "Other",
  description: "Runtime flags that have not been assigned to an admin section yet.",
  flagNames: [],
} as const satisfies RuntimeFlagFallbackGroupDefinition;

export const getRuntimeFlagGroupBySlug = (slug: string): RuntimeFlagGroupDefinition | undefined =>
  RUNTIME_FLAG_GROUPS.find((group) => group.slug === slug);

export const getRuntimeFlagGroupPath = (group: Pick<RuntimeFlagGroupDefinition, "slug">): string =>
  `${RUNTIME_FLAG_ROOT_PATH}/${group.slug}`;

export const groupRuntimeFlags = <TFlag extends { name: string }>(
  flags: readonly TFlag[],
): GroupedRuntimeFlags<TFlag>[] => {
  const flagsByName = new Map(flags.map((flag) => [flag.name, flag]));
  const assignedNames = new Set<string>();
  const groups: GroupedRuntimeFlags<TFlag>[] = [];

  for (const group of RUNTIME_FLAG_GROUPS) {
    const groupFlags: TFlag[] = [];

    for (const flagName of group.flagNames) {
      const flag = flagsByName.get(flagName);
      if (!flag) {
        continue;
      }

      groupFlags.push(flag);
      assignedNames.add(flag.name);
    }

    if (groupFlags.length > 0) {
      groups.push({
        id: group.id,
        slug: group.slug,
        title: group.title,
        description: group.description,
        flags: groupFlags,
      });
    }
  }

  const otherFlags = flags.filter((flag) => !assignedNames.has(flag.name));
  if (otherFlags.length > 0) {
    groups.push({
      id: OTHER_RUNTIME_FLAG_GROUP.id,
      title: OTHER_RUNTIME_FLAG_GROUP.title,
      description: OTHER_RUNTIME_FLAG_GROUP.description,
      flags: otherFlags,
    });
  }

  return groups;
};

export const getRuntimeFlagGroupsForSlug = <TFlag extends { name: string }>(
  groups: readonly GroupedRuntimeFlags<TFlag>[],
  slug: string | undefined,
): {
  selectedGroup: RuntimeFlagGroupDefinition | null;
  isUnknownGroup: boolean;
  groups: GroupedRuntimeFlags<TFlag>[];
} => {
  if (!slug) {
    return { selectedGroup: null, isUnknownGroup: false, groups: [...groups] };
  }

  const selectedGroup = getRuntimeFlagGroupBySlug(slug);
  if (!selectedGroup) {
    return { selectedGroup: null, isUnknownGroup: true, groups: [] };
  }

  return {
    selectedGroup,
    isUnknownGroup: false,
    groups: groups.filter((group) => group.id === selectedGroup.id),
  };
};
